import React, { useState } from 'react';
import axios from 'axios';
import {
  API,
  authHeaders,
  alertError,
  alertSuccess,
  btnPrimary,
  fieldStyle,
  labelStyle,
  pageTitleStyle,
  pageSubtitleStyle,
} from '../api/ticketsApi';
import TicketStatusBadge from '../components/TicketStatusBadge';
import SearchableSelect from '../../../components/SearchableSelect';
import TicketNoLookup from '../components/TicketNoLookup';
import { usePrefillTicketNo } from '../hooks/usePrefillTicketNo';
import { TicketActionBack, TicketDetailLink } from '../ticketNav';
import { useProductFilter } from '../ProductFilterContext';

const STATUS_OPTIONS = [
  { value: 'in_progress', label: 'In progress' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function isStatusEligible(ticket) {
  return ticket && !['closed', 'cancelled'].includes(ticket.status);
}

function TicketUpdateStatusPage() {
  const prefillTicket = usePrefillTicketNo();
  const { productId, selectedProduct } = useProductFilter();
  const [ticketNo, setTicketNo] = useState(prefillTicket || '');
  const [ticket, setTicket] = useState(null);
  const [status, setStatus] = useState('');
  const [updateReason, setUpdateReason] = useState('');
  const [reasonForResolution, setReasonForResolution] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selected = ticket?.ticket_no || ticketNo.trim();
    if (!selected || !status) {
      setError('Enter a ticket number and choose a new status');
      return;
    }
    if (!ticket || !isStatusEligible(ticket)) {
      setError('Lookup a valid open ticket before updating status');
      return;
    }

    if (status === 'completed') {
      if (!updateReason.trim()) {
        setError('Reason for cause is required when completing a ticket');
        return;
      }
      if (!reasonForResolution.trim()) {
        setError('Reason for resolution is required when completing a ticket');
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.patch(
        `${API}/${selected}/status`,
        { status, update_reason: updateReason || null, reason_for_resolution: reasonForResolution || null },
        authHeaders(),
      );
      setSuccess(`Ticket ${selected} updated to ${status.replace('_', ' ')}`);
      setTicketNo('');
      setTicket(null);
      setStatus('');
      setUpdateReason('');
      setReasonForResolution('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: '720px' }}>
      <div className="tk-page-head">
        <div className="tk-page-head-main">
          <div className="tk-page-head-back">
            <TicketActionBack ticketNo={ticket?.ticket_no || ticketNo || prefillTicket || null} />
          </div>
          <h2 style={pageTitleStyle}>Update Status</h2>
          <p style={pageSubtitleStyle}>
            Enter a ticket number to load that ticket only — then choose the new status.
            {selectedProduct ? ` Scoped to ${selectedProduct.name}.` : ''}
          </p>
        </div>
      </div>

      {error && <div style={alertError}>{typeof error === 'string' ? error : JSON.stringify(error)}</div>}
      {success && <div style={alertSuccess}>{success}</div>}

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '14px' }}>
        <TicketNoLookup
          value={ticketNo}
          onChange={(v) => {
            setTicketNo(v);
            setSuccess(null);
            setError(null);
          }}
          onTicket={setTicket}
          filter={isStatusEligible}
          productId={productId}
          label="Ticket number"
          placeholder="e.g. HW-2026-00041"
        />

        {ticket && (
          <div className="tk-field-readonly" style={{ padding: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{ticket.ticket_no}</strong>
              <TicketStatusBadge status={ticket.status} />
            </div>
            <p style={{ margin: '8px 0 0', fontSize: '13px', fontWeight: 500 }}>{ticket.subject}</p>
            <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#44474E' }}>
              {ticket.description || ticket.problem_desc || '—'}
            </p>
            <TicketDetailLink ticketNo={ticket.ticket_no} className="tk-link" style={{ fontSize: '12px' }}>
              View detail →
            </TicketDetailLink>
          </div>
        )}

        <div>
          <label className="tk-label" style={labelStyle}>New Status</label>
          <SearchableSelect
            options={STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
            placeholder="Select status…"
            searchPlaceholder="Filter statuses…"
            aria-label="New status"
          />
        </div>

        <div>
          <label className="tk-label" style={labelStyle}>
            {status === 'completed' ? 'Reason for Cause' : 'Update Reason'}
            {(status === 'completed' || status === 'pending' || status === 'cancelled') && (
              <span style={{ color: '#DC2626' }}> *</span>
            )}
          </label>
          <textarea
            className="tk-field"
            value={updateReason}
            onChange={(e) => setUpdateReason(e.target.value)}
            rows={3}
            style={fieldStyle}
            placeholder={status === 'completed' ? 'What caused the issue?' : 'Why is the status changing?'}
          />
        </div>

        {status === 'completed' && (
          <div>
            <label className="tk-label" style={labelStyle}>
              Reason for Resolution <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <textarea
              className="tk-field"
              value={reasonForResolution}
              onChange={(e) => setReasonForResolution(e.target.value)}
              rows={3}
              style={fieldStyle}
              placeholder="How was the issue resolved?"
            />
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !ticket || !status}
          className="tk-btn tk-btn-primary"
          style={{ ...btnPrimary, width: 'fit-content' }}
        >
          {submitting ? 'Updating…' : 'Update Status'}
        </button>
      </form>
    </div>
  );
}

export default TicketUpdateStatusPage;
